import React, { useState, useRef, useCallback } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import Form, {
  Item,
  ButtonItem,
  ButtonOptions,
  RequiredRule,
  CustomRule,
  CompareRule,
} from "devextreme-react/form";
import LoadIndicator from "devextreme-react/load-indicator";
import notify from "devextreme/ui/notify";
import AUTH_API from "api/authApi";
import { usePost } from "@hooks/useAuthApi";
import "./ChangePasswordForm.scss";
import { showNotification } from "utils/helpers";
import Loader from "@common-components/loader/Loader";

export default function ChangePasswordForm() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const formData = useRef({ password: "", confirmPassword: "" });
  const { recoveryCode } = useParams();
  const location = useLocation();
  const email = location.state?.email;
  const token = location.state?.token;

  const [passwordMode, setPasswordMode] = useState("password");
  const [confirmPasswordMode, setConfirmPasswordMode] = useState("password");

  const { mutate: setPassword } = usePost("setPassword", AUTH_API.SETPASSWORD);

  const getPasswordEditorOptions = (
    passwordMode,
    setPasswordMode,
    placeholder
  ) => ({
    stylingMode: "outlined",
    placeholder,
    mode: passwordMode,
    buttons: [
      {
        name: "lockIcon",
        location: "before",
        options: { icon: "lock", stylingMode: "text" },
      },
      {
        name: "togglePassword",
        location: "after",
        options: {
          icon: passwordMode === "password" ? "eyeopen" : "eyeclose",
          stylingMode: "text",
          onClick: () =>
            setPasswordMode((prev) =>
              prev === "password" ? "text" : "password"
            ),
        },
      },
    ],
  });

  const passwordEditorOptions = getPasswordEditorOptions(
    passwordMode,
    setPasswordMode,
    "Enter New Password"
  );
  const confirmPasswordEditorOptions = getPasswordEditorOptions(
    confirmPasswordMode,
    setConfirmPasswordMode,
    "Confirm Password"
  );

  const validatePassword = (e) => {
    const password = e.value;
    return (
      /[A-Z]/.test(password) &&
      /[a-z]/.test(password) &&
      /\d/.test(password) &&
      /[!@#$%^&*(),.?":{}|<>]/.test(password) &&
      password.length >= 8 &&
      password.length <= 20
    );
  };

  const onSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      setLoading(true);

      setPassword(
        { email, password: formData.current.password, token },
        {
          onSuccess: (response) => {
            if (response.isSuccess) {
              navigate("/login");
              showNotification("Password Changed Successfully", "success");
            } else {
              showNotification(response.errors[0].errorMessageEn, "error");
            }
          },
          onError: (error) => {
            showNotification(error.message, "warning");
          },
          onSettled: () => {
            setLoading(false);
          },
        }
      );
    },
    [navigate, recoveryCode]
  );

  return (
    <>
      <form onSubmit={onSubmit}>
        {/* <p className="text-xl mb-1">Set Password 🔒</p> */}
        {/* <p className="text-xs">Secure Your Account</p> */}
        <Form
          formData={formData.current}
          disabled={loading}
          labelMode="outside"
          labelLocation="top"
          showColonAfterLabel={false}
          className="mt-4"
        >
          <Item
            dataField="password"
            editorType="dxTextBox"
            editorOptions={passwordEditorOptions}
          >
            <RequiredRule message="Password is required" />
            <CustomRule
              message="Password must be 8-20 characters, include at least 1 uppercase letter, 1 lowercase letter, 1 digit, and 1 special character."
              validationCallback={validatePassword}
            />
          </Item>

          <Item
            dataField="confirmPassword"
            editorType="dxTextBox"
            editorOptions={confirmPasswordEditorOptions}
          >
            <RequiredRule message="Confirm Password is required" />
            <CompareRule
              message="Passwords do not match"
              comparisonTarget={() => formData.current.password}
            />
          </Item>

          <ButtonItem>
            <ButtonOptions
              width="100%"
              height="40px"
              type="default"
              useSubmitBehavior
            >
              <span className="dx-button-text">
                {loading ? (
                  <Loader  />
                ) : (
                  "Submit"
                )}
              </span>
            </ButtonOptions>
          </ButtonItem>
        </Form>
      </form>
      <div className="flex items-center justify-between mt-[70px]">
        <p className="m-0 text-xs">
          B’Logic<sup className="ms-[0.5px]">®</sup> <i>i</i>QMS
        </p>
        <p className="m-0 text-xs">All rights reserved - 2025</p>
      </div>
    </>
  );
}
